import { PowerOffIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"

/**
 * "This module is switched off" — ONE component, ONE pair of strings, for every
 * flagged module in the app (§18.2, §18.3).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS: THERE WERE SEVEN WORDINGS FOR ONE SITUATION.
 *
 * Five modules each wrote their own copy, in two families that had drifted into
 * saying different things about the same fact:
 *
 *   `notAvailableTitle`/`notAvailableHelp`  challans, credit notes, payments
 *       → "…ask your son to switch it on when you need it."
 *   `offTitle`/`offHelp`                    messages, notifications
 *       → "Your admin can turn it on under Features."
 *
 * Three things were wrong, and each is fixed here rather than in five places:
 *
 *  1. SEVEN COPIES OF ONE IDEA. The module name is now a PARAMETER. Adding the
 *     sixth flagged module writes no new sentence, and no future edit can reach
 *     four of the five surfaces and miss the fifth.
 *  2. "ASK YOUR SON" ASSUMED WHO WAS HOLDING THE PHONE. The father is the main
 *     user and it read warmly for him — but the SON is the ADMIN, and the app
 *     was telling him to ask himself; the CA would have got it too. The warmth
 *     stays ("nothing has gone wrong"); the assumption goes.
 *  3. "UNDER FEATURES" NAMED A SCREEN THAT DID NOT EXIST — for about a day. The
 *     sentence was written in the expectation that the flags admin screen would
 *     land, committing the very failure this list records as fixed: there was no
 *     `routes.features`, no `/settings/features` in `routes/index.tsx` and no
 *     `FeaturesScreen` under `features/settings/`. Only the API half had shipped
 *     (`apps/api/src/routes/feature-flags.ts`), along with a whole screen's
 *     worth of translated copy — the 40-odd `flags.*` keys in en.json and
 *     mr.json that no `t()` call reached. So the copy was made to name no door,
 *     saying who could switch it on without claiming where.
 *
 *     `/settings/features` is real now (2026-08-17), so the sentence names it
 *     again — one string rather than five. `feature-off-copy.test.ts` is a
 *     TWO-WAY PIN on exactly that: it fails if the copy names a screen the route
 *     table does not mount, AND it fails if the screen exists and the copy has
 *     not caught up. Do not edit either half alone.
 *
 *     WHO CAN ACTUALLY WALK THROUGH THAT DOOR IS STILL A SEPARATE QUESTION, and
 *     the sentence is worded for it. The flags screen is gated on
 *     `settings.write`, which the OWNER and the CA do not hold — she has no
 *     Settings row in her rail at all. So the copy still leads with WHO ("anyone
 *     with admin access") and names the place second: the reader who cannot go
 *     there still learns there is a person to ask, and the ADMIN who can gets
 *     told where. Naming a place without naming a person would have been the old
 *     failure wearing a working link.
 *
 * IT SAYS "NOT SWITCHED ON", NEVER "NOT ALLOWED". §18.2 makes a disabled
 * feature a 404 rather than a 403 precisely so "forbidden" cannot leak that
 * unreleased work is sitting in the deployed build; a permission message here
 * would put that leak back on the screen by hand.
 *
 * AND IT OFFERS NO RETRY. Pressing a button cannot flip a flag, and a control
 * that never works is how someone concludes the app is broken rather than
 * merely incomplete.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export interface FeatureOffProps {
  /**
   * The module's own name, ALREADY TRANSLATED — `t("nav.creditNotes")`,
   * `t("chat.title")`. A key would be the wrong parameter: two of the five call
   * sites name their module from a screen title rather than a nav label, and a
   * component that took keys would have to know which.
   */
  module: string
  /** The module's own icon, so the panel still looks like the screen it replaces. */
  icon?: React.ReactNode
  className?: string
}

export function FeatureOff({ module, icon, className }: FeatureOffProps): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Empty data-slot="feature-off" className={className}>
      <EmptyHeader>
        <EmptyMedia variant="icon">{icon ?? <PowerOffIcon />}</EmptyMedia>
        <EmptyTitle>{t("flags.moduleOffTitle", { module })}</EmptyTitle>
        <EmptyDescription>{t("flags.moduleOffHelp")}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}
